import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, tokenStore, baseTokenStore } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // user states: undefined = loading, null = unauth, object = authed
  const [user, setUser] = useState(undefined);
  // privacy mode: vendor hides prices while with a customer
  const [privacy, setPrivacy] = useState(() => localStorage.getItem("lc_privacy_mode") === "1");

  const togglePrivacy = () => {
    setPrivacy((p) => {
      const n = !p;
      localStorage.setItem("lc_privacy_mode", n ? "1" : "0");
      return n;
    });
  };

  const fetchMe = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      tokenStore.clear();
      baseTokenStore.clear();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) {
        tokenStore.set(data.access_token);
        baseTokenStore.set(data.access_token);
      }
      const u = { ...data };
      delete u.access_token;
      delete u.refresh_token;
      setUser(u);
      return { ok: true, user: u };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  // Open a vendor (seller) session with 4-digit PIN
  const selectVendor = async (vendorId, pin) => {
    try {
      const { data } = await api.post("/auth/vendor-session", { vendor_id: vendorId, pin });
      tokenStore.set(data.access_token);
      setUser((u) => ({ ...u, vendor: data.vendor }));
      return { ok: true, vendor: data.vendor };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const setupVendorPin = async (vendorId, pin) => {
    try {
      const { data } = await api.post("/auth/vendor-pin/setup", { vendor_id: vendorId, pin });
      tokenStore.set(data.access_token);
      setUser((u) => ({ ...u, vendor: data.vendor }));
      return { ok: true, vendor: data.vendor };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const switchCompany = async (companyId) => {
    try {
      const { data } = await api.post("/auth/select-company", { company_id: companyId });
      tokenStore.set(data.access_token);
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  // Open the company admin space with its 4-digit PIN
  const enterAdminSpace = async (pin) => {
    try {
      const { data } = await api.post("/auth/admin-session", { pin });
      tokenStore.set(data.access_token);
      setUser((u) => ({ ...u, admin_space: true }));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  // First-time setup of the company admin PIN (then opens the admin space)
  const setupAdminPin = async (pin) => {
    try {
      const { data } = await api.post("/auth/admin-pin/setup", { pin });
      tokenStore.set(data.access_token);
      setUser((u) => ({ ...u, admin_space: true, admin_pin_set: true }));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  // Back to profile selection: restore the company (base) token
  const exitVendor = () => {
    const base = baseTokenStore.get();
    if (base) tokenStore.set(base);
    setUser((u) => {
      if (!u) return u;
      const { vendor, admin_space, ...rest } = u;
      return rest;
    });
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    tokenStore.clear();
    baseTokenStore.clear();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, selectVendor, setupVendorPin, switchCompany, enterAdminSpace, setupAdminPin, exitVendor, refresh: fetchMe, privacy, togglePrivacy }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
